import { useQuery } from '@tanstack/react-query';
import { DateTime } from 'luxon';
import { FC, useState } from 'react';
import slugify from 'slugify';
import styled from 'styled-components';

import ExportButton from '@/components/ExportButton';
import FiltersDropdown from '@/components/FilterDropdown';
import FilterButton from '@/components/FilterDropdown/FilterButton';
import FilterRadioButton from '@/components/FilterDropdown/FilterRadioButton';
import {
  FilterGroupBody,
  FilterGroupHeader,
  FilterGroupTitle,
} from '@/components/FilterDropdown/Parts';
import {
  PageHeading,
  SectionHeading,
  TeamHeading,
} from '@/components/Layout/Parts';
import {
  ResponsiveTable,
  SortableTh,
  Table,
  TableContent,
  Td,
  Th,
} from '@/components/Table';
import Text from '@/components/Text';
import { ChannelFinancesResource, SortState } from '@/types/resources';
import { api } from '@/utils/api';
import { SortDirection } from '@/utils/enums';
import { numFormatter } from '@/utils/helpers';

import { getDateTimeObjectFromString, months } from '../Finances';
import AllChannelsTableRow from './components/AllChannelsTableRow';

const Header = styled.div`
  display: flex;
  margin: 0 0 12px;
  align-items: flex-end;
  ${SectionHeading} {
    margin-right: auto;
    margin-bottom: 0;
  }
`;

const StyledTable = styled(Table)`
  th,
  td {
    &:nth-child(1) {
      width: 40%;
    }
    &:nth-child(2) {
      width: 20%;
    }
    &:nth-child(3) {
      width: 20%;
    }
    &:nth-child(4) {
      width: 20%;
    }
  }
`;

const AggregatedTable = styled(StyledTable)`
  margin-bottom: 16px;
`;

const Strong = styled.strong`
  font-weight: 700;
`;

const FilterWrap = styled.div`
  position: relative;
`;

const ButtonsWrap = styled.div`
  display: flex;
  gap: 8px;
`;

const LastUpdateBadge = styled.div`
  display: inline-flex;
  align-items: center;
  height: 32px;
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.grayscaleDark};
  margin-right: 8px;
  padding: 0 12px;
`;

const Dot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.success};
  margin-right: 8px;
`;

export enum Periods {
  LAST_7_DAYS = '-7 days',
  LAST_30_DAYS = '-30 days',
  LAST_90_DAYS = '-90 days',
  LAST_YEAR = '-1 year',
  ALL_TIME = '',
}

const renderPeriodTitle = (period: Periods) => {
  switch (period) {
    case Periods.LAST_7_DAYS:
      return 'Last 7 days';
    case Periods.LAST_30_DAYS:
      return 'Last 30 days';
    case Periods.LAST_90_DAYS:
      return 'Last 90 days';
    case Periods.LAST_YEAR:
      return 'Last year';
    default:
      return 'All time';
  }
};

export interface AllChannelsData {
  channels: Omit<
    ChannelFinancesResource,
    'total_revenue' | 'published_revenue'
  >[];
  last_update?: string;
  total_views?: number;
}

const AllChannels: FC = () => {
  const [sort, setSort] = useState<SortState>({
    direction: SortDirection.DESC,
    property: 'total_views',
  });

  const [activePeriod, setActivePeriod] = useState({
    type: 'month',
    period: months[0].toFormat('yyyy-LL-dd'),
  });

  const [showFilters, setShowFilters] = useState(false);

  const { data, isLoading, isError } = useQuery<AllChannelsData>(
    [
      'all_channels',
      {
        sort,
        period: activePeriod.period,
      },
    ],
    () =>
      api.get<never, AllChannelsData>(`/v1/channels/all`, {
        params: {
          ...activePeriod,
          sortDirection: sort.direction,
          sortProperty: sort.property,
        },
      }),
    {
      staleTime: 5 * 60 * 1000,
    },
  );

  const fetchCsv = async () =>
    api.get<never, string>(`/v1/channels/all/export`, {
      params: {
        ...activePeriod,
        sortDirection: sort.direction,
        sortProperty: sort.property,
      },
    });

  const renderSectionHeading = () => {
    const { period, type } = activePeriod;

    if (type === 'month') {
      const dt = DateTime.fromFormat(period, 'yyyy-LL-dd');

      const startDate = dt.startOf('month').toFormat('dd/LL/yyyy');

      const endDate = dt.endOf('month').toFormat('dd/LL/yyyy');

      return `${dt.toFormat('yyyy LLLL')} (${startDate} - ${endDate})`;
    }

    const dt = getDateTimeObjectFromString(period);

    if (!dt) {
      return renderPeriodTitle(period as Periods);
    }

    const startDate = dt.toFormat('dd/LL/yyyy');

    const endDate = DateTime.now().toFormat('dd/LL/yyyy');

    return `${renderPeriodTitle(
      period as Periods,
    )} (${startDate} - ${endDate})`;
  };

  return (
    <>
      <TeamHeading />
      <PageHeading>All Channels</PageHeading>
      <Header>
        <SectionHeading>{renderSectionHeading()}</SectionHeading>
        {!!data?.last_update && (
          <LastUpdateBadge>
            <Dot />
            <Text as="span" $type="smallHeadline">
              Last update:{' '}
              {DateTime.fromISO(data.last_update).toFormat('dd/LL/yyyy')}
            </Text>
          </LastUpdateBadge>
        )}
        <ButtonsWrap>
          <>
            <ExportButton
              fileName={`all-channels-${
                !!activePeriod.period
                  ? slugify(activePeriod.period)
                  : 'all_time'
              }.csv`}
              fetchCsv={fetchCsv}
            />
          </>
          <FilterWrap>
            <FilterButton
              isActive={showFilters}
              onClick={(e) => {
                e?.stopPropagation();
                setShowFilters((prev) => !prev);
              }}
              filterApplied
            />
            {showFilters && (
              <FiltersDropdown onClose={() => setShowFilters(false)}>
                <FilterGroupHeader>
                  <FilterGroupTitle>By Period:</FilterGroupTitle>
                </FilterGroupHeader>
                <FilterGroupBody>
                  {Object.values(Periods).map((period, index) => (
                    <FilterRadioButton
                      key={index}
                      id={`periodRadio${index}`}
                      value={period}
                      checked={activePeriod.period === period}
                      setValue={(newValue) =>
                        setActivePeriod({
                          period: newValue,
                          type: 'dateTimeString',
                        })
                      }
                    >
                      {renderPeriodTitle(period)}
                    </FilterRadioButton>
                  ))}
                </FilterGroupBody>
                <FilterGroupHeader>
                  <FilterGroupTitle>By Month:</FilterGroupTitle>
                </FilterGroupHeader>
                <FilterGroupBody>
                  {months.map((dt, index) => (
                    <FilterRadioButton
                      key={index}
                      id={`monthRadio${index}`}
                      value={dt.toFormat('yyyy-LL-dd')}
                      checked={
                        activePeriod.period === dt.toFormat('yyyy-LL-dd')
                      }
                      setValue={(newValue) =>
                        setActivePeriod({
                          period: newValue,
                          type: 'month',
                        })
                      }
                    >
                      {dt.toFormat('yyyy LLLL')}
                    </FilterRadioButton>
                  ))}
                </FilterGroupBody>
              </FiltersDropdown>
            )}
          </FilterWrap>
        </ButtonsWrap>
      </Header>
      <ResponsiveTable>
        <AggregatedTable $fixed>
          <thead>
            <tr>
              <Th>All Channels</Th>
              <Th>Total Views</Th>
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={2}
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.channels?.length}
            >
              <tr>
                <Td>
                  {activePeriod.type === 'month' ? (
                    <>
                      Month:{' '}
                      <Strong>
                        {DateTime.fromFormat(
                          activePeriod.period,
                          'yyyy-LL-dd',
                        ).toFormat('LLLL')}
                      </Strong>
                    </>
                  ) : (
                    <>
                      <Strong>
                        {renderPeriodTitle(activePeriod.period as Periods)}
                      </Strong>
                    </>
                  )}
                </Td>
                <Td>
                  {data?.total_views ? numFormatter(data.total_views) : '-'}
                </Td>
              </tr>
            </TableContent>
          </tbody>
        </AggregatedTable>
      </ResponsiveTable>
      <ResponsiveTable>
        <StyledTable $fixed>
          <thead>
            <tr>
              <Th>
                Channel name
                <Text as="div" $type="extraSmall" $color="gray">
                  Channel link
                </Text>
              </Th>
              <SortableTh
                setSort={setSort}
                sort={sort}
                disableDefault
                property="total_views"
                title="Total Views"
              />
            </tr>
          </thead>
          <tbody>
            <TableContent
              colSpan={2}
              isLoading={isLoading}
              isError={isError}
              hasData={!!data?.channels?.length}
            >
              {data?.channels?.map((channel) => (
                <AllChannelsTableRow key={channel.id} channel={channel} />
              ))}
            </TableContent>
          </tbody>
        </StyledTable>
      </ResponsiveTable>
    </>
  );
};

export default AllChannels;
